import { useCallback, useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../api/client';
import { ensureFormDraft, getFormDraft, publishForm, saveFormDraft, updateForm } from '../api/forms';
import { evaluateFormVisibility } from '../utils/formVisibility';
import { useConfirm } from './ui/ConfirmDialog';
import { CheckGroup, CheckRow } from './ui/CheckRow';
import {
  FORM_LIMITS,
  OPERATORS_BY_SOURCE_TYPE,
  OPERATOR_LABELS,
  QUESTION_TYPES,
  QUESTION_TYPE_LABELS,
  type ConditionOperator,
  type FormAnswers,
  type FormDraft,
  type FormTemplateListItem,
  type QuestionType,
} from '../types/forms';
import type { Store } from '../types/stores';
import {
  attentionOperatorsForType,
  availableConditionSources,
  defaultAttention,
  draftToModel,
  modelToSaveInput,
  moveInArray,
  supportsAttention,
  usesOptions,
  type EditCondition,
  type EditModel,
  type EditQuestion,
  type EditSection,
} from '../utils/formBuilderModel';

// Fase 23A: constructor de formularios. Tres pestanas (Configuracion / Constructor / Vista previa).
// Trabaja sobre un modelo local con "keys" de referencia (utils/formBuilderModel); al guardar arma el
// SaveDraftInput y el backend valida/normaliza (autoritativo). La Vista Previa usa el MISMO evaluador
// de visibilidad que usara 23B (espejo puro). Reordenamiento con botones accesibles (sin deps nuevas).

let keyCounter = 0;
const newKey = (p: string) => `${p}_${Date.now().toString(36)}_${(keyCounter += 1)}`;

export function FormBuilder({ template, stores, onBack, onChanged }: { template: FormTemplateListItem; stores: Store[]; onBack: () => void; onChanged: () => void }) {
  const confirm = useConfirm();
  const [tab, setTab] = useState<'config' | 'build' | 'preview'>('config');
  const [draft, setDraft] = useState<FormDraft | null>(null);
  const [model, setModel] = useState<EditModel>({ sections: [], questions: [], conditions: [] });
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | undefined>(template.updatedAt);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [dirty, setDirty] = useState(false);

  // Config (metadata) state.
  const [meta, setMeta] = useState({ name: template.name, description: template.description ?? '', allStores: template.appliesToAllStores, storeIds: new Set(template.storeIds) });

  const loadDraft = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const res = await getFormDraft(template.id);
      if (res.draft) { setDraft(res.draft); setModel(draftToModel(res.draft)); }
      else { setDraft(null); setModel({ sections: [], questions: [], conditions: [] }); }
    } catch (e) { setError(getErrorMessage(e, 'No se pudo cargar el borrador.')); }
    finally { setLoading(false); }
  }, [template.id]);

  useEffect(() => { void loadDraft(); }, [loadDraft]);

  const createDraft = async () => {
    setBusy(true); setError(undefined);
    try {
      const res = await ensureFormDraft(template.id);
      setDraft(res.draft); setModel(draftToModel(res.draft)); setDirty(false);
      onChanged();
    } catch (e) { setError(getErrorMessage(e, 'No se pudo crear el borrador.')); }
    finally { setBusy(false); }
  };

  const update = (fn: (m: EditModel) => EditModel) => { setModel((m) => fn(structuredClone(m))); setDirty(true); };

  const questionCount = model.questions.length;
  const sectionCount = model.sections.length;

  const save = async (): Promise<boolean> => {
    setBusy(true); setError(undefined); setNotice(undefined);
    try {
      const res = await saveFormDraft(template.id, modelToSaveInput(model, expectedUpdatedAt));
      setDraft(res.draft); setModel(draftToModel(res.draft)); setDirty(false);
      onChanged();
      setNotice('Borrador guardado.');
      return true;
    } catch (e) { setError(getErrorMessage(e, 'No se pudo guardar el borrador.')); return false; }
    finally { setBusy(false); }
  };

  const saveMeta = async () => {
    setBusy(true); setError(undefined); setNotice(undefined);
    try {
      const res = await updateForm(template.id, {
        name: meta.name.trim(),
        description: meta.description.trim() || null,
        storeIds: meta.allStores ? [] : [...meta.storeIds],
        expectedUpdatedAt,
      });
      setExpectedUpdatedAt(res.template.updatedAt);
      onChanged();
      setNotice('Configuracion guardada.');
    } catch (e) { setError(getErrorMessage(e, 'No se pudo guardar la configuracion.')); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (dirty) { const ok = await save(); if (!ok) return; }
    if (!(await confirm({ title: 'Publicar versión', description: 'Una vez publicada no se puede editar; para cambios se crea una nueva versión. ¿Publicar esta versión?', confirmLabel: 'Publicar' }))) return;
    setBusy(true); setError(undefined);
    try {
      const res = await publishForm(template.id, expectedUpdatedAt);
      setExpectedUpdatedAt(res.template.updatedAt);
      onChanged();
      setNotice(`Version ${res.publishedVersionNumber} publicada.`);
      await loadDraft();
    } catch (e) { setError(getErrorMessage(e, 'No se pudo publicar.')); }
    finally { setBusy(false); }
  };

  return (
    <section className="form-builder" aria-label={`Constructor de ${template.name}`}>
      <div className="form-builder__header">
        <button className="text-button" onClick={onBack} type="button">← Volver a formularios</button>
        <h2>{template.name}</h2>
        {template.activeVersion && <span className="form-badge form-badge--published">v{template.activeVersion.versionNumber} publicada</span>}
        {draft && <span className="form-badge form-badge--draft">Borrador v{draft.versionNumber}</span>}
      </div>

      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}

      <div className="form-builder__tabs" role="tablist" aria-label="Secciones del constructor">
        {(['config', 'build', 'preview'] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`form-tab ${tab === t ? 'form-tab--active' : ''}`} onClick={() => setTab(t)} type="button">
            {t === 'config' ? 'Configuracion' : t === 'build' ? 'Constructor' : 'Vista previa'}
          </button>
        ))}
      </div>

      {loading ? <div className="inline-loading"><span className="spinner" />Cargando…</div> : (
        <>
          {tab === 'config' && (
            <ConfigTab meta={meta} setMeta={setMeta} stores={stores} onSave={saveMeta} busy={busy} />
          )}

          {tab === 'build' && (
            !draft ? (
              <div className="empty-panel">
                <strong>Sin borrador editable</strong>
                <span>{template.activeVersion ? 'Crea un borrador (nueva version) para editar; la version publicada no cambia.' : 'Crea el primer borrador para empezar a construir el formulario.'}</span>
                <button className="button button--primary" disabled={busy} onClick={() => void createDraft()} type="button">Crear borrador</button>
              </div>
            ) : (
              <BuilderTab
                model={model}
                update={update}
                counts={{ sectionCount, questionCount }}
                onSave={save}
                busy={busy}
                dirty={dirty}
                onPublish={publish}
              />
            )
          )}

          {tab === 'preview' && (
            draft ? <PreviewTab model={model} dirty={dirty} /> : <div className="empty-panel empty-panel--small"><strong>Sin borrador</strong><span>Crea un borrador en el Constructor para probar la vista previa.</span></div>
          )}
        </>
      )}
    </section>
  );
}

// ---------------- Config tab ----------------
function ConfigTab({ meta, setMeta, stores, onSave, busy }: { meta: { name: string; description: string; allStores: boolean; storeIds: Set<string> }; setMeta: React.Dispatch<React.SetStateAction<{ name: string; description: string; allStores: boolean; storeIds: Set<string> }>>; stores: Store[]; onSave: () => void; busy: boolean }) {
  return (
    <div className="form-config">
      <label className="field"><span>Nombre del formulario *</span><input value={meta.name} maxLength={150} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} /></label>
      <label className="field"><span>Descripcion</span><textarea value={meta.description} maxLength={1000} rows={3} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} /></label>
      {/* P-076: renglones de selección de 44 px (radio o casilla y su etiqueta juntos) en vez de controles nativos de
          13 px sin objetivo táctil. Mismo `name`, mismo estado y misma semántica. */}
      <CheckGroup legend="Tiendas donde aplica">
        <CheckRow checked={meta.allStores} label="Todas las tiendas del negocio" name="scope" onChange={() => setMeta((m) => ({ ...m, allStores: true }))} type="radio" value="all" />
        <CheckRow checked={!meta.allStores} label="Tiendas especificas" name="scope" onChange={() => setMeta((m) => ({ ...m, allStores: false }))} type="radio" value="some" />
        {!meta.allStores && (
          <CheckGroup legend="Tiendas">
            {stores.map((s) => (
              <CheckRow checked={meta.storeIds.has(s.id)} key={s.id} label={s.name} onChange={(checked) => setMeta((m) => { const next = new Set(m.storeIds); if (checked) next.add(s.id); else next.delete(s.id); return { ...m, storeIds: next }; })} />
            ))}
          </CheckGroup>
        )}
      </CheckGroup>
      <div className="form-builder__actions"><button className="button button--primary" disabled={busy || !meta.name.trim()} onClick={onSave} type="button">Guardar configuracion</button></div>
    </div>
  );
}

// ---------------- Builder tab ----------------
function BuilderTab({ model, update, counts, onSave, busy, dirty, onPublish }: {
  model: EditModel;
  update: (fn: (m: EditModel) => EditModel) => void;
  counts: { sectionCount: number; questionCount: number };
  onSave: () => Promise<boolean>;
  busy: boolean;
  dirty: boolean;
  onPublish: () => void;
}) {
  const addSection = () => update((m) => { m.sections.push({ key: newKey('s'), title: `Seccion ${m.sections.length + 1}`, description: '' }); return m; });
  const moveSection = (i: number, dir: -1 | 1) => update((m) => { m.sections = moveInArray(m.sections, i, dir); return m; });
  const removeSection = (key: string) => update((m) => { m.sections = m.sections.filter((s) => s.key !== key); m.questions = m.questions.filter((q) => q.sectionKey !== key); m.conditions = m.conditions.filter((c) => !(c.targetType === 'SECTION' && c.targetKey === key)); return m; });

  return (
    <div className="form-build">
      <div className="form-counts">
        <span>Secciones: {counts.sectionCount} / {FORM_LIMITS.MAX_SECTIONS}</span>
        <span>Preguntas: {counts.questionCount} / {FORM_LIMITS.MAX_QUESTIONS}</span>
      </div>

      {model.sections.length === 0 && <div className="empty-panel empty-panel--small"><strong>Sin secciones</strong><span>Agrega la primera seccion para empezar.</span></div>}

      {model.sections.map((section, si) => (
        <SectionEditor key={section.key} section={section} index={si} total={model.sections.length} model={model} update={update} onMove={moveSection} onRemove={removeSection} />
      ))}

      <div className="form-build__add">
        <button className="button button--ghost" disabled={counts.sectionCount >= FORM_LIMITS.MAX_SECTIONS} onClick={addSection} type="button">+ Agregar seccion</button>
        {counts.sectionCount >= FORM_LIMITS.MAX_SECTIONS && <small>Limite de {FORM_LIMITS.MAX_SECTIONS} secciones alcanzado.</small>}
      </div>

      <div className="form-builder__actions form-builder__actions--sticky">
        <button className="button button--primary" disabled={busy || !dirty} onClick={() => void onSave()} type="button">{dirty ? 'Guardar borrador' : 'Guardado'}</button>
        <button className="button button--secondary" disabled={busy} onClick={onPublish} type="button">Publicar version</button>
      </div>
    </div>
  );
}

function SectionEditor({ section, index, total, model, update, onMove, onRemove }: {
  section: EditSection; index: number; total: number; model: EditModel;
  update: (fn: (m: EditModel) => EditModel) => void;
  onMove: (i: number, dir: -1 | 1) => void; onRemove: (key: string) => void;
}) {
  const questions = model.questions.filter((q) => q.sectionKey === section.key);
  const addQuestion = () => update((m) => {
    if (m.questions.length >= FORM_LIMITS.MAX_QUESTIONS) return m;
    m.questions.push({ key: newKey('q'), sectionKey: section.key, text: '', type: 'SHORT_TEXT', isRequired: false, helpText: '', options: [], attention: null });
    return m;
  });
  const atQuestionLimit = model.questions.length >= FORM_LIMITS.MAX_QUESTIONS;

  return (
    <fieldset className="form-section-card">
      <legend className="form-section-card__legend">Seccion {index + 1}</legend>
      <div className="form-section-card__head">
        <label className="field field--grow"><span>Titulo de la seccion *</span><input value={section.title} maxLength={200} onChange={(e) => update((m) => { const s = m.sections.find((x) => x.key === section.key); if (s) s.title = e.target.value; return m; })} /></label>
        <div className="form-reorder" role="group" aria-label={`Reordenar seccion ${index + 1}`}>
          <button className="icon-button" aria-label="Subir seccion" disabled={index === 0} onClick={() => onMove(index, -1)} type="button">↑</button>
          <button className="icon-button" aria-label="Bajar seccion" disabled={index === total - 1} onClick={() => onMove(index, 1)} type="button">↓</button>
          <button className="text-button text-button--danger" onClick={() => onRemove(section.key)} type="button">Eliminar</button>
        </div>
      </div>
      <label className="field"><span>Descripcion (opcional)</span><input value={section.description} maxLength={1000} onChange={(e) => update((m) => { const s = m.sections.find((x) => x.key === section.key); if (s) s.description = e.target.value; return m; })} /></label>

      <ConditionEditor model={model} update={update} targetType="SECTION" targetKey={section.key} label="Mostrar esta seccion si" />

      <div className="form-questions">
        {questions.map((q, qi) => (
          <QuestionEditor key={q.key} question={q} index={qi} total={questions.length} model={model} update={update} />
        ))}
      </div>
      <div className="form-build__add">
        <button className="button button--ghost button--small" disabled={atQuestionLimit} onClick={addQuestion} type="button">+ Agregar pregunta</button>
        {atQuestionLimit && <small>Limite de {FORM_LIMITS.MAX_QUESTIONS} preguntas alcanzado.</small>}
      </div>
    </fieldset>
  );
}

function QuestionEditor({ question, index, total, model, update }: {
  question: EditQuestion; index: number; total: number; model: EditModel;
  update: (fn: (m: EditModel) => EditModel) => void;
}) {
  const mutate = (fn: (q: EditQuestion) => void) => update((m) => { const q = m.questions.find((x) => x.key === question.key); if (q) fn(q); return m; });
  const move = (dir: -1 | 1) => update((m) => {
    const inSection = m.questions.filter((q) => q.sectionKey === question.sectionKey);
    const others = m.questions.filter((q) => q.sectionKey !== question.sectionKey);
    const pos = inSection.findIndex((q) => q.key === question.key);
    m.questions = [...others, ...moveInArray(inSection, pos, dir)];
    return m;
  });
  const remove = () => update((m) => { m.questions = m.questions.filter((q) => q.key !== question.key); m.conditions = m.conditions.filter((c) => !(c.targetType === 'QUESTION' && c.targetKey === question.key) && c.sourceQuestionKey !== question.key); return m; });
  const addOption = () => mutate((q) => { if (q.options.length < FORM_LIMITS.MAX_OPTIONS_PER_QUESTION) q.options.push({ key: newKey('o'), label: '' }); });

  return (
    <div className="form-question-card">
      <div className="form-question-card__head">
        <label className="field field--grow"><span>Pregunta *</span><input value={question.text} maxLength={300} placeholder="Escribe la pregunta" onChange={(e) => mutate((q) => { q.text = e.target.value; })} /></label>
        <div className="form-reorder" role="group" aria-label={`Reordenar pregunta ${index + 1}`}>
          <button className="icon-button" aria-label="Subir pregunta" disabled={index === 0} onClick={() => move(-1)} type="button">↑</button>
          <button className="icon-button" aria-label="Bajar pregunta" disabled={index === total - 1} onClick={() => move(1)} type="button">↓</button>
          <button className="text-button text-button--danger" onClick={remove} type="button">Eliminar</button>
        </div>
      </div>
      <div className="form-question-card__row">
        <label className="field"><span>Tipo</span>
          <select value={question.type} onChange={(e) => mutate((q) => { q.type = e.target.value as QuestionType; if (!usesOptions(q.type)) q.options = []; q.attention = null; })}>
            {QUESTION_TYPES.map((t) => <option key={t} value={t}>{QUESTION_TYPE_LABELS[t]}</option>)}
          </select>
        </label>
        <CheckRow checked={question.isRequired} label="Obligatoria" onChange={(checked) => mutate((q) => { q.isRequired = checked; })} plain />
      </div>
      <label className="field"><span>Texto de ayuda (opcional)</span><input value={question.helpText} maxLength={500} onChange={(e) => mutate((q) => { q.helpText = e.target.value; })} /></label>

      {usesOptions(question.type) && (
        <fieldset className="form-options">
          <legend>Opciones ({question.options.length} / {FORM_LIMITS.MAX_OPTIONS_PER_QUESTION})</legend>
          {question.options.map((opt, oi) => (
            <div key={opt.key} className="form-option-row">
              <input aria-label={`Opcion ${oi + 1}`} value={opt.label} maxLength={200} placeholder={`Opcion ${oi + 1}`} onChange={(e) => mutate((q) => { const o = q.options.find((x) => x.key === opt.key); if (o) o.label = e.target.value; })} />
              <button className="icon-button" aria-label="Subir opcion" disabled={oi === 0} onClick={() => mutate((q) => { const j = oi - 1; [q.options[oi], q.options[j]] = [q.options[j]!, q.options[oi]!]; })} type="button">↑</button>
              <button className="icon-button" aria-label="Bajar opcion" disabled={oi === question.options.length - 1} onClick={() => mutate((q) => { const j = oi + 1; [q.options[oi], q.options[j]] = [q.options[j]!, q.options[oi]!]; })} type="button">↓</button>
              <button className="text-button text-button--danger" onClick={() => mutate((q) => { q.options = q.options.filter((x) => x.key !== opt.key); })} type="button">Quitar</button>
            </div>
          ))}
          <button className="button button--ghost button--small" disabled={question.options.length >= FORM_LIMITS.MAX_OPTIONS_PER_QUESTION} onClick={addOption} type="button">+ Agregar opcion</button>
        </fieldset>
      )}

      <ConditionEditor model={model} update={update} targetType="QUESTION" targetKey={question.key} label="Mostrar esta pregunta si" />

      <AttentionEditor question={question} mutate={mutate} />
    </div>
  );
}

// ---------------- Attention rule editor (Fase 23C) ----------------
// "Supervision": marca una respuesta enviada como que REQUIERE ATENCION. Es parte de la version (no
// retroactiva). Una regla por pregunta. Solo aplica a tipos con operadores de atencion.
function AttentionEditor({ question, mutate }: { question: EditQuestion; mutate: (fn: (q: EditQuestion) => void) => void }) {
  if (!supportsAttention(question.type)) {
    return (
      <div className="form-attention form-attention--na">
        <span className="form-attention__label">Supervision</span>
        <small className="form-conditions__empty">Este tipo de pregunta no admite regla de atencion.</small>
      </div>
    );
  }
  const a = question.attention;
  const operators = attentionOperatorsForType(question.type);
  const usesOptionValue = question.type === 'SINGLE_SELECT' || question.type === 'MULTI_SELECT';
  const usesNumberValue = question.type === 'NUMBER';
  const isYesNo = question.type === 'YES_NO';
  const toggle = (on: boolean) => mutate((q) => { q.attention = on ? defaultAttention(q.type) : null; });

  return (
    <div className="form-attention">
      <CheckRow checked={!!a} label="Marcar como “requiere atencion” cuando…" onChange={toggle} plain />
      {a && (
        <div className="form-condition-row form-attention__row">
          <select aria-label="Operador de atencion" value={a.operator} onChange={(e) => mutate((q) => { if (q.attention) q.attention.operator = e.target.value as ConditionOperator; })}>
            {operators.map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
          </select>
          {isYesNo && (
            <select aria-label="Valor de atencion" value={a.valueText ?? 'true'} onChange={(e) => mutate((q) => { if (q.attention) q.attention.valueText = e.target.value; })}>
              <option value="true">Si</option><option value="false">No</option>
            </select>
          )}
          {usesOptionValue && (
            <select aria-label="Opcion de atencion" value={a.valueOptionKey ?? ''} onChange={(e) => mutate((q) => { if (q.attention) q.attention.valueOptionKey = e.target.value || null; })}>
              <option value="">Elige opcion</option>
              {question.options.map((o) => <option key={o.key} value={o.key}>{o.label || '(sin texto)'}</option>)}
            </select>
          )}
          {usesNumberValue && (
            <input aria-label="Valor numerico de atencion" type="number" value={a.valueNumber ?? ''} onChange={(e) => mutate((q) => { if (q.attention) q.attention.valueNumber = e.target.value === '' ? null : Number(e.target.value); })} />
          )}
        </div>
      )}
      {a && <small className="form-attention__hint">Se evalua solo cuando la pregunta es visible y fue respondida. No es retroactivo.</small>}
    </div>
  );
}

// ---------------- Condition editor ----------------
function ConditionEditor({ model, update, targetType, targetKey, label }: {
  model: EditModel; update: (fn: (m: EditModel) => EditModel) => void;
  targetType: 'QUESTION' | 'SECTION'; targetKey: string; label: string;
}) {
  const conditions = model.conditions.filter((c) => c.targetType === targetType && c.targetKey === targetKey);
  // Origenes validos: preguntas ANTERIORES en orden logico (regla hacia atras) y de tipo con operadores.
  const availableSources = useMemo(() => availableConditionSources(model, targetType, targetKey), [model, targetType, targetKey]);

  const addCondition = () => {
    const source = availableSources[0];
    if (!source) return;
    update((m) => { m.conditions.push({ key: newKey('c'), targetType, targetKey, sourceQuestionKey: source.key, operator: OPERATORS_BY_SOURCE_TYPE[source.type][0]!, valueOptionKey: null, valueNumber: null, valueText: source.type === 'YES_NO' ? 'true' : null }); return m; });
  };

  return (
    <div className="form-conditions">
      <div className="form-conditions__label">{label} <span className="form-conditions__hint">(todas se cumplen — Y)</span></div>
      {availableSources.length === 0 && conditions.length === 0 && <small className="form-conditions__empty">Solo puedes condicionar con una pregunta anterior. Agrega preguntas antes de esta.</small>}
      {conditions.map((c) => {
        const source = model.questions.find((q) => q.key === c.sourceQuestionKey);
        const operators = source ? OPERATORS_BY_SOURCE_TYPE[source.type] : [];
        const showsOptionValue = c.operator === 'EQUALS' || c.operator === 'NOT_EQUALS' || c.operator === 'CONTAINS' || c.operator === 'NOT_CONTAINS';
        const showsNumberValue = ['GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL'].includes(c.operator) || (c.operator === 'EQUALS' && source?.type === 'NUMBER');
        const mutate = (fn: (cc: EditCondition) => void) => update((m) => { const cc = m.conditions.find((x) => x.key === c.key); if (cc) fn(cc); return m; });
        return (
          <div key={c.key} className="form-condition-row">
            <select aria-label="Pregunta origen" value={c.sourceQuestionKey} onChange={(e) => mutate((cc) => { cc.sourceQuestionKey = e.target.value; const s = model.questions.find((q) => q.key === e.target.value); cc.operator = s ? OPERATORS_BY_SOURCE_TYPE[s.type][0]! : cc.operator; cc.valueOptionKey = null; cc.valueNumber = null; cc.valueText = s?.type === 'YES_NO' ? 'true' : null; })}>
              {availableSources.map((q) => <option key={q.key} value={q.key}>{q.text || '(sin texto)'}</option>)}
              {!availableSources.some((q) => q.key === c.sourceQuestionKey) && source && <option value={source.key}>{source.text || '(sin texto)'}</option>}
            </select>
            <select aria-label="Operador" value={c.operator} onChange={(e) => mutate((cc) => { cc.operator = e.target.value as ConditionOperator; })}>
              {operators.map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>)}
            </select>
            {source?.type === 'YES_NO' && c.operator === 'EQUALS' && (
              <select aria-label="Valor" value={c.valueText ?? 'true'} onChange={(e) => mutate((cc) => { cc.valueText = e.target.value; })}><option value="true">Si</option><option value="false">No</option></select>
            )}
            {showsOptionValue && source && usesOptions(source.type) && (
              <select aria-label="Opcion" value={c.valueOptionKey ?? ''} onChange={(e) => mutate((cc) => { cc.valueOptionKey = e.target.value || null; })}>
                <option value="">Elige opcion</option>
                {source.options.map((o) => <option key={o.key} value={o.key}>{o.label || '(sin texto)'}</option>)}
              </select>
            )}
            {showsNumberValue && (
              <input aria-label="Valor numerico" type="number" value={c.valueNumber ?? ''} onChange={(e) => mutate((cc) => { cc.valueNumber = e.target.value === '' ? null : Number(e.target.value); })} />
            )}
            <button className="text-button text-button--danger" onClick={() => update((m) => { m.conditions = m.conditions.filter((x) => x.key !== c.key); return m; })} type="button">Quitar</button>
          </div>
        );
      })}
      {availableSources.length > 0 && conditions.length < FORM_LIMITS.MAX_CONDITIONS_PER_TARGET && (
        <button className="text-button" onClick={addCondition} type="button">+ Agregar condicion</button>
      )}
      {conditions.length >= FORM_LIMITS.MAX_CONDITIONS_PER_TARGET && <small>Limite de {FORM_LIMITS.MAX_CONDITIONS_PER_TARGET} condiciones.</small>}
    </div>
  );
}

// ---------------- Preview tab ----------------
function PreviewTab({ model, dirty }: { model: EditModel; dirty: boolean }) {
  const [answers, setAnswers] = useState<FormAnswers>({});
  // Estructura tipo FormDraft usando keys como ids (consistente para el evaluador espejo).
  const preview = useMemo(() => {
    const draftLike = {
      sections: model.sections.map((s, i) => ({ id: s.key, key: s.key, title: s.title, description: s.description, position: i })),
      questions: model.questions.map((q, i) => ({ id: q.key, key: q.key, sectionId: q.sectionKey, text: q.text, type: q.type, isRequired: q.isRequired, helpText: q.helpText, position: i, options: q.options.map((o, oi) => ({ id: o.key, key: o.key, label: o.label, position: oi })) })),
      conditions: model.conditions.map((c, i) => ({ id: c.key, targetType: c.targetType, targetQuestionId: c.targetType === 'QUESTION' ? c.targetKey : null, targetSectionId: c.targetType === 'SECTION' ? c.targetKey : null, sourceQuestionId: c.sourceQuestionKey, operator: c.operator, valueOptionId: c.valueOptionKey, valueNumber: c.valueNumber, valueText: c.valueText, position: i })),
    };
    return { draftLike, vis: evaluateFormVisibility(draftLike, answers) };
  }, [model, answers]);

  const setAnswer = (qKey: string, value: FormAnswers[string]) => setAnswers((a) => ({ ...a, [qKey]: value }));

  return (
    <div className="form-preview">
      <div className="form-preview__banner" role="status">VISTA PREVIA — las respuestas no se guardaran</div>
      {dirty && <div className="alert alert--warning" role="status">Estas viendo tus cambios sin guardar. Se evaluan con la misma logica que usara el formulario real.</div>}
      {model.sections.map((s) => {
        if (!preview.vis.visibleSectionIds.has(s.key)) return null;
        const qs = model.questions.filter((q) => q.sectionKey === s.key && preview.vis.visibleQuestionIds.has(q.key));
        return (
          <fieldset key={s.key} className="form-preview__section">
            <legend>{s.title || '(seccion sin titulo)'}</legend>
            {s.description && <p className="form-preview__desc">{s.description}</p>}
            {qs.length === 0 && <p className="form-preview__desc">—</p>}
            {qs.map((q) => (
              <div key={q.key} className="form-preview__q">
                <label className="form-preview__qlabel">{q.text || '(pregunta sin texto)'}{q.isRequired && <span className="form-preview__req" aria-label="obligatoria"> *</span>}</label>
                {q.helpText && <small className="form-preview__help">{q.helpText}</small>}
                <PreviewInput q={q} value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} />
              </div>
            ))}
          </fieldset>
        );
      })}
      {model.sections.length === 0 && <div className="empty-panel empty-panel--small"><strong>Sin contenido</strong><span>Agrega secciones y preguntas en el Constructor.</span></div>}
    </div>
  );
}

function PreviewInput({ q, value, onChange }: { q: EditQuestion; value: FormAnswers[string]; onChange: (v: FormAnswers[string]) => void }) {
  switch (q.type) {
    case 'YES_NO':
      return (
        <div className="form-preview__yn n3-check-group__items" role="group" aria-label={q.text}>
          <CheckRow checked={value === true} label="Si" name={q.key} onChange={() => onChange(true)} type="radio" value="si" />
          <CheckRow checked={value === false} label="No" name={q.key} onChange={() => onChange(false)} type="radio" value="no" />
        </div>
      );
    case 'SHORT_TEXT': return <input value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'LONG_TEXT': return <textarea rows={3} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'NUMBER': return <input type="number" value={typeof value === 'number' ? value : ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'DATE': return <input type="date" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'TIME': return <input type="time" value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />;
    case 'SINGLE_SELECT':
      return (
        <select value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">—</option>
          {q.options.map((o) => <option key={o.key} value={o.key}>{o.label || '(sin texto)'}</option>)}
        </select>
      );
    case 'MULTI_SELECT': {
      const arr = Array.isArray(value) ? value : [];
      return (
        <div className="form-preview__multi n3-check-group__items" role="group" aria-label={q.text}>
          {q.options.map((o) => (
            <CheckRow checked={arr.includes(o.key)} key={o.key} label={o.label || '(sin texto)'} onChange={(checked) => onChange(checked ? [...arr, o.key] : arr.filter((x) => x !== o.key))} />
          ))}
        </div>
      );
    }
    default: return null;
  }
}
